<template>
    <div class="mb-4">
        <card class="filter mb-3" body-classes="pb-3">
            <base-input>
                <el-select v-model="selectedCompetencies"
                        multiple
                        class="select-info"
                        :placeholder="$t('feedbacks.select-competencies')"
                        @change="filtersChanged">

                    <el-option v-for="competency in competencies"
                            :key="competency.id"
                            :label="competency['name_'+registration.degree.lang]"
                            :value="competency.id"
                            class="select-primary"
                    >
                        {{ competency['name_'+registration.degree.lang] }}
                    </el-option>

                </el-select>
            </base-input>
            <div class="row pt-3">
                <div class="col-md-6">
                    <base-checkbox class="mb-3 d-inline-block mr-3" v-model="showPositions">
                        {{ $t('feedbacks.positions') }}
                    </base-checkbox>

                    <base-checkbox class="mb-3 d-inline-block mr-3" v-model="showNotes">
                        {{ $t('feedbacks.notes') }}
                    </base-checkbox>

                    <base-checkbox class="mb-3 d-inline-block" v-model="showPictures">
                        {{ $t('feedbacks.pictures') }}
                    </base-checkbox>
                </div>
                <div class="col-md-6 text-right">
                    <base-button type="white mr-1" size="sm" @click="selectAll">
                        {{ $t('common.select-all') }}
                    </base-button>
                    
                    <base-button type="white" size="sm" @click="unselectAll">
                        {{ $t('common.unselect-all') }}
                    </base-button>
                </div>
            </div>
        </card>

        <div class="card feedback mb-0 p-4">
            
            <div v-if="!filteredFeedbacks.length">
                <p class="text-center">{{ $t('feedbacks.no-feedback') }}</p>
            </div>
            
            <div v-else class="timeline timeline-one-side" data-timeline-content="axis" data-timeline-axis-style="dashed">
                <div v-for="(item, index) in filteredFeedbacks" :key="index">
                    <div class="timeline-block"
                        :class="{'timeslot-block': !item.type, 'feedback-block': item.type}">

                        <span class="timeline-step badge-warning" v-if="firstPosition(item)">
                            <i class="fas fa-play"></i>
                        </span>

                        <span class="timeline-step badge-warning" v-else-if="secondPosition(item)">
                            <i class="fas fa-sync-alt"></i>
                        </span>

                        <span class="timeline-step badge-default" v-else>
                            <i class="fas fa-thumbtack"></i>
                        </span>
                        
                        <div class="timeline-content">

                            <feedback v-if="item.type"
                                :feedback="item" :lang="registration.degree.lang"
                                :edit="edit" :sharing="sharing"
                                :learner="learner"
                                :registration="registration"
                                :simulation="simulation"
                                :session="session"
                                @update="update"
                                @remove="remove"
                                @share="share"
                            >
                                <template slot="position" v-if="item.type == 'position' && !learner">
                                    <h6 class="text-sm mb-0" v-if="session.learner1">
                                        {{ session.learner1.name }}: {{ learnerPosition(item, session.learner1) }}
                                    </h6>
                                    <h6 class="text-sm" v-if="session.learner2">
                                        {{ session.learner2.name }}: {{ learnerPosition(item, session.learner2) }}
                                    </h6>
                                </template>

                                <template slot="position" v-if="item.type == 'position' && learner">
                                    <h6 class="text-sm">
                                        {{ $t('feedbacks.position') }}: {{ learnerPosition(item) }}
                                    </h6>
                                </template>

                            </feedback>

                            <timeslot-feedbacks v-if="!item.type"
                                :timeslot="item" :lang="registration.degree.lang"
                                :edit="edit" :sharing="sharing"
                                :selectedCompetencies="selectedCompetencies"
                                :showPositions="showPositions"
                                :showNotes="showNotes"
                                :showPictures="showPictures"
                                :learner="learner"
                                :registration="registration"
                                :simulation="simulation"
                                :session="session"
                                @update="update"
                                @remove="remove"
                                @share="share"
                                @createNote="createNote"
                                @createPicture="createPicture"
                                @createIssue="$emit('createIssue', $event)"
                            >
                            </timeslot-feedbacks>
                        </div>
                    </div>
                </div>
            </div>

            <div class="text-center mt-4" v-if="edit">

                <base-button type="primary" @click.prevent="createPosition()">
                    <i class="fas fa-plus mr-1"></i> {{ $t('feedbacks.position') }}
                </base-button>

                <base-button type="primary" @click.prevent="createNote()">
                    <i class="fas fa-plus mr-1"></i> {{ $t('feedbacks.note') }}
                </base-button>

                <base-button type="primary" @click.prevent="createPicture()">
                    <i class="fas fa-plus mr-1"></i> {{ $t('feedbacks.picture') }}
                </base-button>
            </div>

            <portal to="modals" v-if="edit">

                <form-modal v-model="modals.editPosition"
                    :title="$t('feedbacks.position')"
                    :loading="positionForm.loading"
                    :centered="false"
                >
                    <position-form v-model="positionForm.data"
                        :errors="positionForm.errors"
                        :modal="modals.editPosition"
                        :position-names="positionNames"
                        :registration="registration"
                        :session="session"
                        :learner="learner"
                    >
                    </position-form>
                </form-modal>

                <form-modal v-model="modals.editNote"
                    :title="$t('feedbacks.note')"
                    :loading="noteForm.loading"
                    size="lg"
                    :centered="false"
                >
                    <note-form v-model="noteForm.data"
                        :errors="noteForm.errors"
                        :modal="modals.editNote"
                        :registration="registration"
                        :session="session"
                        :competencies="competenciesSelect"
                    >
                    </note-form>
                </form-modal>

                <form-modal v-model="modals.editPicture"
                    :title="$t('feedbacks.picture')"
                    :loading="pictureForm.loading"
                    size="lg"
                    :centered="false"
                >
                    <picture-form v-model="pictureForm.data"
                        :errors="pictureForm.errors"
                        :modal="modals.editPicture"
                        :registration="registration"
                        :session="session"
                        :competencies="competenciesSelect"
                    >
                    </picture-form>
                </form-modal>

                <form-modal v-model="modals.editPictureFile"
                    :title="$t('feedbacks.picture')"
                    :loading="pictureForm.loading"
                    size="lg"
                    :centered="false"
                >
                    <picture-file-form v-model="pictureForm.data"
                        :errors="pictureForm.errors"
                        :modal="modals.editPictureFile"
                        :registration="registration"
                        :session="session"
                        :competencies="competenciesSelect"
                        :endpoint="endpoint"
                        @change="list"
                    >
                    </picture-file-form>
                </form-modal>

                <confirm-modal v-model="modals.confirmDelete">
                    <p>{{ $t('feedbacks.delete-confirm') }}</p>
                </confirm-modal>

            </portal>

        </div>
    </div>
</template>

<script>
    import ModalsController from '@trax/classes/ModalsController'
    import CrudForm from '@trax/classes/CrudForm'
    import CrudClient from '@trax/classes/CrudClient'
    import PositionForm from './PositionForm'
    import NoteForm from './NoteForm'
    import PictureForm from './PictureForm'
    import PictureFileForm from './PictureFileForm'
    import Feedback from './Feedback'
    import TimeslotFeedbacks from './TimeslotFeedbacks'

    export default {

        components: {
            PositionForm, NoteForm, PictureForm, PictureFileForm, Feedback, TimeslotFeedbacks
        },

        model: {
            prop: 'session',
            event: 'change'
        },

        props: {
            
            registration: {
                type: Object,
                required: true
            },
            
            simulation: {
                type: Object,
                required: true
            },

            session: {
                type: Object,
                default: null
            },

            learner: {
                type: Object,
                default: null
            },
            
            competencies: {
                type: Array,
                default: null
            },

            edit: {
                type: Boolean,
                default: false
            },            

            sharing: {
                type: Boolean,
                default: false
            },            
        },

        data() {
            let endpoint = '/astral/api/front/dashboard/feedbacks'
            let client = this.learner
                ? new CrudClient(endpoint, {
                    registration_id: this.registration.id,
                    simulation_id: this.simulation.id,
                    learner_id: this.learner.id,
                    session_id: this.session ? this.session.id : null,
                })
                : new CrudClient(endpoint, {
                    registration_id: this.registration.id,
                    session_id: this.session.id,
                })
            return {
                endpoint,
                client,
                positionForm: new CrudForm(client, this.mapPosition, this.unmapPosition),
                noteForm: new CrudForm(client, this.mapNote, this.unmapNote),
                pictureForm: new CrudForm(client, this.mapPicture, this.unmapPicture),
                modals: new ModalsController(['confirmDelete', 'editPosition', 'editNote', 'editPicture', 'editPictureFile']),
                feedbacks: [],
                loaded: false,
                selectedCompetencies: [],
                showNotes: true,
                showPictures: true,
                showPositions: true,
                savedFilters: this.$auth.getUiConfig('file_filters')
            }
        },

        created() {
            this.positionForm.init(this, 'positionForm')
            this.noteForm.init(this, 'noteForm')
            this.pictureForm.init(this, 'pictureForm')
            this.modals.init(this, 'modals')
            this.restoreFilters()
            this.list()
        },

        watch: {
            // Transversal navigation causes change of IDs without reloading the component.
            // So we need to refresh the component manually.

            '$route.params.simulationId': function (id) {
                this.client.query.simulation_id = id
                this.client.query.session_id = this.session ? this.session.id : null
                this.list()
            },

            '$route.params.learnerId': function (id) {
                this.client.query.learner_id = id
                this.client.query.session_id = this.session ? this.session.id : null
                this.list()
            },

            showPositions(newVal) {
                this.filtersChanged()
            },

            showNotes(newVal) {
                this.filtersChanged()
            },

            showPictures(newVal) {
                this.filtersChanged()
            }
        },

        computed: {

            filteredFeedbacks() {
                return this.feedbacks.filter(item => {
                    return (item.type && this.itemVisible(item))
                     || (item.feedbacks && item.feedbacks.length && this.itemsVisible(item.feedbacks))
                })
            },

            competenciesSelect() {
                let select = {}
                for (let index in this.competencies) {
                    select[this.competencies[index].id] = this.competencies[index]['name_'+this.registration.degree.lang]
                }
                return select
            },

            positionNames() {
                let positions = {}
                positions[this.registration.degree.simulation_role_1] = this.registration.degree.simulation_role_1_name[this.registration.degree.lang]
                if (this.registration.degree.simulation_role_2_name) {
                    positions[this.registration.degree.simulation_role_2] = this.registration.degree.simulation_role_2_name[this.registration.degree.lang]
                }
                return positions
            },
        },

        methods: {

            firstPosition(item) {
                let positions = this.filteredFeedbacks.filter(item => item.type == 'position')
                if (positions.length == 0) {
                    return false
                }
                return item.id == positions[0].id
            },

            secondPosition(item) {
                return item.type == 'position' && !this.firstPosition(item)
            },

            itemVisible(item) {
                if (this.selectedCompetencies.length == 0) {
                    return this.itemTypeVisible(item)
                }
                if (item.type == 'position') {
                    return this.itemTypeVisible(item)
                }
                const itemCompetencies = item.competencies.map(competency => competency.id)
                const intersect = this.selectedCompetencies.filter(x => itemCompetencies.includes(x))
                return intersect.length > 0
                    ? this.itemTypeVisible(item)
                    : false
            },

            itemTypeVisible(item) {
                return (item.type == 'position' && this.showPositions)
                    || (item.type == 'note' && this.showNotes)
                    || (item.type == 'picture' && this.showPictures)
            },

            itemsVisible(items) {
                for (let index in items) {
                    if (this.itemVisible(items[index])) {
                        return true
                    }
                }
                return false
            },

            selectAll() {
                this.selectedCompetencies = this.competencies.map(competency => competency.id)
                this.filtersChanged()
            },

            unselectAll(session) {
                this.selectedCompetencies = []
                this.filtersChanged()
            },

            filtersChanged() {
                this.rememberFilters()
            },

            rememberFilters() {
                this.$auth.setUiConfig(this.filters(), 'file_filters')
            },

            restoreFilters() {
                if (this.savedFilters) {
                    this.selectedCompetencies = this.savedFilters.selectedCompetencies
                    this.showPositions = this.savedFilters.showPositions
                    this.showNotes = this.savedFilters.showNotes
                    this.showPictures = this.savedFilters.showPictures
                }
            },

            filters() {
                return {
                    selectedCompetencies: this.selectedCompetencies,
                    showPositions: this.showPositions,
                    showNotes: this.showNotes,
                    showPictures: this.showPictures,
                }
            },

            learnerPosition(feedback, learner) {
                learner = learner ? learner : this.learner
                return learner.id == feedback.learner1_id
                     ? this.positionNames[feedback.simulation_role_1]
                     : this.positionNames[feedback.simulation_role_2]
            },
            
            list() {
                this.loaded = false
                this.client.list().then(resp => {          
                    this.feedbacks = resp.data.data
                    this.loaded = true
                    this.$emit('sharedFeedbacks', resp.data.included.shared_feedbacks)
                })
            },

            createPosition() {
                this.modals.open('editPosition')
                this.positionForm.create().then(resp => {
                    this.modals.close('editPosition')
                    this.list()
                })
            },

            createNote(timeslotId = null) {
                let data = null
                if (timeslotId) {
                    data = this.mapNote()
                    data.timeslot_id = timeslotId
                }
                this.modals.open('editNote')
                this.noteForm.create(data).then(resp => {
                    this.modals.close('editNote')
                    this.list()
                })
            },

            createPicture(timeslotId = null) {
                let data = null
                if (timeslotId) {
                    data = this.mapPicture()
                    data.timeslot_id = timeslotId
                }
                this.modals.open('editPictureFile')
                this.pictureForm.create(data).then(resp => {
                    this.modals.close('editPictureFile')
                    this.list()
                })
            },

            share(feedback) {
                if (feedback.type == 'note') {
                    let data = this.mapData(feedback, 'note')
                    this.client.update(this.unmapNote(data)).then(resp => {
                        this.list()
                    })
                } else if (feedback.type == 'picture') {
                    let data = this.mapData(feedback, 'picture')
                    this.client.update(this.unmapPicture(data)).then(resp => {
                        this.list()
                    })
                }
            },

            update(feedback) {
                if (feedback.type == 'note') {
                    this.updateNote(feedback.id)
                } else if (feedback.type == 'picture') {
                    this.updatePicture(feedback.id)
                } else if (feedback.type == 'position') {
                    this.updatePosition(feedback.id)
                }
            },

            updatePosition(id) {
                this.modals.open('editPosition')
                this.positionForm.update(id).then(resp => {
                    this.modals.close('editPosition')
                    this.list()
                })
            },

            updateNote(id) {
                this.modals.open('editNote')
                this.noteForm.update(id).then(resp => {
                    this.modals.close('editNote')
                    this.list()
                })
            },

            updatePicture(id) {
                this.modals.open('editPicture')
                this.pictureForm.update(id).then(resp => {
                    this.modals.close('editPicture')
                    this.list()
                })
            },

            remove(feedback) {
                this.modals.open('confirmDelete').then(resp => {
                    if (resp.confirmed) {
                        this.client.delete(feedback.id).then(resp => {
                            this.list()
                        })
                    }
                })
            },

            mapPosition(position) {
                return this.mapData(position, 'position')
            },

            mapNote(note) {
                return this.mapData(note, 'note')
            },

            mapPicture(picture) {
                let data = this.mapData(picture, 'picture')
                // We need to add this now because data will be directly sent by the form,
                // without passing by the unmapData method.
                data.registration_id = this.registration.id
                data.simulation_id = this.simulation.id
                if (this.session) {
                    data.session_id = this.session.id
                    data.learner1_id = this.session.learner1_id
                    data.learner2_id = this.session.learner2_id
                } else {
                    data.learner1_id = this.learner.id
                }
                return data
            },

            mapData(feedback, type) {
                return {
                    type: type,
                    id: feedback ? feedback.id : null,
                    log_time_from: feedback ? feedback.log_time_from : null,
                    log_time_to: feedback ? feedback.log_time_to : null,
                    learner_summary: feedback ? feedback.learner_summary : null,
                    instructor_summary: feedback ? feedback.instructor_summary : null,
                    public_description: feedback ? feedback.public_description : null,
                    confidential_description: feedback ? feedback.confidential_description : null,
                    competency_ids: feedback
                        ? feedback.competencies.map(competency => competency.id.toString())
                        : null,
                    personal: feedback ? feedback.personal == 1 : true,
                    learner1_check: feedback ? feedback.learner1_id != null : (this.learner && this.session && this.learner.id == this.session.learner1_id),
                    learner2_check: feedback ? feedback.learner2_id != null : (this.learner && this.session && this.learner.id == this.session.learner2_id),
                    shared: feedback ? feedback.shared == 1 : false,
                    simulation_role_1: feedback ? feedback.simulation_role_1 : null,
                    simulation_role_2: feedback ? feedback.simulation_role_2 : null,
                    timeslot_id: feedback ? feedback.timeslot_id : null,
                }
            },

            unmapPosition(data) {
                let position = this.unmapData(data, 'position')
                if (this.session) {
                    position.learner1_id = this.session.learner1_id
                    position.learner2_id = this.session.learner2_id
                }
                return position
            },

            unmapNote(data) {
                return this.unmapData(data, 'note')
            },

            unmapPicture(data) {
                return this.unmapData(data, 'picture')
            },

            unmapData(data, type) {
                data.type = type
                data.registration_id = this.registration.id
                data.simulation_id = this.simulation.id
                if (this.session) {
                    data.session_id = this.session.id
                    data.learner1_id = data.learner1_check ? this.session.learner1_id : null
                    data.learner2_id = data.learner2_check ? this.session.learner2_id : null
                } else {
                    data.learner1_id = this.learner.id
                }
                return data
            }
        }
    }
</script>

<style scoped>

    .filter >>> .form-group {
        margin-bottom: 0;
    }
    .filter >>> .el-select.select-info .el-select__tags .el-tag {
        background: #5e72e4;
    }
    .description {
        white-space: pre-wrap;
    }
    .badge-warning {
        background-color: #ff3709;
        color: #fff;
    }
    @media (min-width: 992px) {
        .timeline-one-side .timeline-content {
            max-width: none;
        }
    }
    .timeslot-block {
        margin-bottom: 10px;
    }
    .feedback-block {
        margin-bottom: 30px;
    }

</style>
